import { FetchCacheItemSources } from '../../../../types';
import {
  FetchInvoiceGqlQuery,
  FetchInvoicesCacheKey,
  InvoiceID,
  InvoiceNanoID,
  InvoiceUUID
} from '../../invoicesTypes';

import { useCacheShowQuery } from '../../../common/reactQuery/hooks/useCacheShowQuery';

import { InvoicesPermissions } from '../../invoicesConstants';

export interface FetchInvoiceResponse<FetchInvoiceType> {
  status: string;
  invoice: FetchInvoiceType;
  error?: Error;
}

export interface CacheInvoiceOptions<ResponseType> {
  invoiceUuid: InvoiceUUID | InvoiceNanoID | InvoiceID;
  cacheKey: FetchInvoicesCacheKey;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    onSuccess?: (data: { invoice: ResponseType }) => void;
    staleTime?: number;
  };
  query: FetchInvoiceGqlQuery;
}

const itemKey = 'invoice';

function useCacheInvoice<FetchInvoiceType>({
  invoiceUuid,
  cacheKey,
  options = {},
  query
}: CacheInvoiceOptions<FetchInvoiceType>) {
  const {
    isOptimistic,
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateCacheShowQueryItemCache
  } = useCacheShowQuery<
    FetchInvoiceResponse<FetchInvoiceType>,
    FetchInvoiceType
  >({
    action: InvoicesPermissions.READ_INVOICE_SHOW_MONGO_QUERY,
    cacheKey,
    cacheQuery: query,
    itemKey,
    options,
    query,
    source: FetchCacheItemSources.DB,
    uuid: invoiceUuid,
    useCacheApiOnly: true
  });

  return {
    isOptimistic,
    invoice: item,
    invoiceErrorData: itemErrorData,
    invoiceError: itemError,
    invoiceFetched: itemFetched,
    invoiceFetching: itemFetching,
    invoiceLoading: itemLoading,
    invoiceIsPlaceholderData: itemIsPlaceholderData,
    refetchInvoice: refetchItem,
    updateInvoiceCache: updateCacheShowQueryItemCache
  };
}

export default useCacheInvoice;
